/* eslint-disable no-plusplus */
import { Component, ViewChild, ElementRef, ComponentRef, OnInit, OnDestroy } from '@angular/core';
import { BooleanStateService } from 'src/app/modules/core/services/boolean-state.service';
import { CommunicationService } from 'src/app/services/communication';
import { ParameterHandlerService, CacheService } from 'projects/common/src/app/services';
import { IMachineStatus } from 'src/app/modules/widget/interfaces/machine-status';
import { MachineContextService } from 'src/app/modules/core/services/parameter/machine-context/machine-context.service';
import { getMoment } from 'src/app/modules/core/services/datetime.service';
import { environment } from 'src/environments/environment';
import { DashboardWidgetBasicComponent } from '../../../dashboard-widget-basic/dashboard-widget-basic.component';
import { COMPANIES_ID } from 'projects/common/src/app/enumerables/companies-id';
import { objectPathExists } from 'projects/common/src/app/services/utils/utilities';
import { UserDataService } from 'src/app/modules/core/services/parameter';
import { ScreenViewService } from 'src/app/modules/widget/services/screen-view.service';
import { BehaviorSubject } from 'rxjs';
import { DashboardFacade } from 'src/app/modules/core/store/dashboard/dashboard.facade';

@Component({
  selector: 'app-machine-status-group',
  templateUrl: './machine-status-group.component.html',
  styleUrls: ['./machine-status-group.component.scss'],
})
export class MachineStatusGroupComponent extends DashboardWidgetBasicComponent implements OnInit, OnDestroy {
  @ViewChild('machineStatus', { read: ElementRef, static: false })
  public machineStatusView: ElementRef;

  // BehaviorSubject to store the collection of machines and update the view when the collection changes
  public collection$: BehaviorSubject<IMachineStatus[]> = new BehaviorSubject<IMachineStatus[]>([]);
  public availableStatus: any[];
  public machineComponents: ComponentRef<any>[];
  public activeStatus: string;
  public visibleData = false;
  public machines: IMachineStatus[];
  public isHcm = false;
  public isScoop: any;
  public dataTraduction: string;
  public animate = false;
  public displayMessage = false;

  constructor(
    public booleanBarService: BooleanStateService,
    public communication: CommunicationService,
    public parameterService: ParameterHandlerService,
    public cacheService: CacheService,
    public machineContext: MachineContextService,
    private userDataService: UserDataService,
    protected screenViewService: ScreenViewService,
    public dashboardFacade: DashboardFacade,
  ) {
    super(booleanBarService, communication, parameterService, cacheService, machineContext, screenViewService);
    this.initParams();

    // Subscribe to the collection$ BehaviorSubject to update the view when the collection changes
    this.collection$.subscribe((collection) => {
      try {
        if (collection.length > 0) {
          this.statusSelection(this.activeStatus);
        } else {
          this.hasData = false;
          this.displayMessage = true;
        }
      } catch (error) {
        throw error;
      }
    });

    this.activeStatus = 'all';

    const hcmState = [
      { value: null, name: 'all', key: 'all', color: 'none' },
      { value: null, name: 'operative', key: 'operativo', color: 'green' },
      { value: null, name: 'operational-delay', key: 'demora-operativa', color: 'orange' },
      { value: null, name: 'non-operational', key: 'no-operativo', color: 'blue' },
      { value: null, name: 'maintenance', key: 'mantenimiento', color: 'gray' },
    ];

    const companyId = this.cacheService.local('session').user.company.id;
    const ishcm = companyId === COMPANIES_ID.HCM;

    this.availableStatus = ishcm ? hcmState : [];

    const date = this.parameterService.parameters.item('date').value as string;

    if (!date) {
      this.parameterService.parameterSource.item('date').next(getMoment(new Date(), `${environment.dateFormatSystem}`));
    }
  }

  /**
   * Init request params
   */
  private initParams(): void {
    this.params = {
      hasDate: true,
      hasMachineType: true,
      hasShift: true,
      hasCompany: true,
      hasZone: true,
    };
  }

  /**
   * Function to build view
   *
   * @param values anything
   */
  public buildView(values: any): void {
    super.buildView(values);
    this.initCollection(values);
    this.statusSelection(this.activeStatus);
  }

  /**
   * Function to change the machines by status
   *
   * @param statusKey: Status key name
   */
  public statusSelection(statusKey: string): void {
    this.activeStatus = statusKey;
    let collection: IMachineStatus[];

    this.animate = false;

    if (statusKey && statusKey !== 'all') {
      // Filter the collection by status without mutating the original collection
      collection = this.collection$
        .getValue()
        .filter((machine) => {
          const statusName = machine.statusFamilyName.replace(/ /gi, '-').toLowerCase();
          return statusName === statusKey;
        })
        .map((machine) => {
          // create a copy of the machine to avoid mutations
          const copiedMachine = { ...machine };

          return copiedMachine;
        });
    } else {
      collection = [...this.collection$.getValue()]; // create a copy of the collection to avoid mutations
    }

    setTimeout(() => {
      this.animate = true;
    }, 0);

    this.loadComponentData(collection);
  }

  /**
   * Function to load machine status component
   */
  public loadComponentData(collection?: IMachineStatus[]): void {
    this.destroyAllComponents();

    // if the collection is not passed as a parameter, get the collection from the BehaviorSubject
    if (!collection) {
      collection = this.collection$.getValue();
    }

    // verify if the collection has data
    this.visibleData = collection && collection.length > 0;

    if (this.visibleData) {
      // wipe out duplicate machines
      collection = collection.filter((v, i) => collection.indexOf(v) === i);

      // assign the machines to the component
      this.isHcm = objectPathExists(this.userDataService.get(), 'company.id', true) === COMPANIES_ID.HCM;
      this.isScoop = collection.map((m) => m.machineTypeId === 4);
      this.machines = collection;
    }
  }

  /**
   * Function to clear the machineComponents array and destroy its components
   */
  public destroyAllComponents(): void {
    if (this.machineStatusView && this.machineStatusView.nativeElement) {
      const { nativeElement } = this.machineStatusView;
      while (nativeElement.firstChild) {
        nativeElement.removeChild(nativeElement.firstChild);
      }
    }
  }
  /**
   * Load the status type to machine in collection
   */
  public initCollection(machineStatusList: IMachineStatus[]): void {
    // Hacer una copia profunda de la colección para evitar mutaciones
    const newCollection = JSON.parse(JSON.stringify(machineStatusList));
    this.hasData = false;

    // Completar el estado de la máquina
    this.visibleData = newCollection.length > 0;
    if (this.visibleData) {
      this.hasData = true;
      if (newCollection.length > 8) {
        // Crear un objeto para contar los elementos por estado
        const countByStatus = {};

        // Contar la cantidad de elementos por estado
        for (const value of newCollection) {
          const statusFamily = value.statusFamilyName.replace(/\s+/g, '-').toLowerCase();
          if (!countByStatus[statusFamily]) {
            countByStatus[statusFamily] = {
              name: value.statusFamilyName,
              color: value.statusFamilyColor,
              count: 0,
            };
          }
          countByStatus[statusFamily].count++;
        }

        // Crear la lista de estados disponibles basados en los conteos agrupados
        this.availableStatus = Object.keys(countByStatus).map((statusFamily) => ({
          statusFamilyName: countByStatus[statusFamily].name.replace(/\s+/g, '-').toLowerCase(),
          statusFamilyColor: countByStatus[statusFamily].color,
          value: countByStatus[statusFamily].count,
        }));

        // Agregar el total
        const total = {
          statusFamilyName: 'all',
          statusFamilyColor: '#eaeaea', // Color predeterminado para el total
          value: newCollection.length,
        };
        this.availableStatus.unshift(total);
      }

      try {
        // Mantener el statusImage original y reemplazar los espacios en statusFamilyName con guiones
        newCollection.forEach((element) => {
          const originalStatusImage = element.statusImage;
          element.statusFamilyName = element.statusFamilyName.replace(/\s+/g, '-').toLowerCase();
          element.statusImage = originalStatusImage;
        });

        // Reducir la colección para evitar máquinas duplicadas
        const reducedCollection = newCollection.reduce((acc, machine) => {
          if (!acc.some((v) => v.machineId === machine.machineId)) {
            acc.push(machine);
          }
          return acc;
        }, []);

        // Ordenar la colección por nombre de máquina
        reducedCollection.sort((a, b) => a.machineName.localeCompare(b.machineName));

        // Emitir la nueva colección al BehaviorSubject
        this.collection$.next(reducedCollection);
      } catch (error) {
        console.error('Data initialization error in machine status group component', error);
      }
    } else {
      this.destroyAllComponents();
      this.collection$.next([]);
      this.displayMessage = true;
    }
  }

  public ngOnDestroy(): void {
    super.ngOnDestroy();
  }
}
